import { useEffect, useRef, useState } from "react";
import {
  fromPriceInput,
  PriceFilterFields,
  toPriceInput,
} from "./PriceFilter";

export type PriceModalValues = {
  minPrice: number | null;
  maxPrice: number | null;
};

type Props = {
  open: boolean;
  appliedMinPrice: number | null;
  appliedMaxPrice: number | null;
  onClose: () => void;
  onApply: (values: PriceModalValues) => void;
};

/**
 * 툴바 ≡ 버튼 → 가격 필터 모달.
 * (종류 태그는 TypeTagBar 로 빠져나갔다 — 예전 TagFilterModal 의 Price 섹션만 남긴 것)
 */
export function PriceFilterModal({
  open,
  appliedMinPrice,
  appliedMaxPrice,
  onClose,
  onApply,
}: Props) {
  const [draftMinStr, setDraftMinStr] = useState(toPriceInput(appliedMinPrice));
  const [draftMaxStr, setDraftMaxStr] = useState(toPriceInput(appliedMaxPrice));
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setDraftMinStr(toPriceInput(appliedMinPrice));
    setDraftMaxStr(toPriceInput(appliedMaxPrice));
  }, [open, appliedMinPrice, appliedMaxPrice]);

  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    const t = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  const draftMinPrice = fromPriceInput(draftMinStr);
  const draftMaxPrice = fromPriceInput(draftMaxStr);
  const hasDraft = draftMinPrice != null || draftMaxPrice != null;

  const handleApply = () => {
    onApply({ minPrice: draftMinPrice, maxPrice: draftMaxPrice });
    onClose();
  };

  const handleClear = () => {
    setDraftMinStr("");
    setDraftMaxStr("");
  };

  if (!open) return null;

  return (
    <div
      className="poca-tag-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="poca-price-modal-title"
    >
      <button
        type="button"
        className="poca-tag-modal__backdrop"
        onClick={onClose}
        aria-label="Close"
        tabIndex={-1}
      />
      <div className="poca-tag-modal__panel" ref={panelRef}>
        <header className="poca-tag-modal__header">
          <h2 id="poca-price-modal-title" className="poca-tag-modal__title">
            Price
          </h2>
          <button
            type="button"
            className="poca-tag-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <section className="poca-tag-modal__section poca-tag-modal__section--price">
          <PriceFilterFields
            className="poca-price--modal"
            minStr={draftMinStr}
            maxStr={draftMaxStr}
            onMinChange={setDraftMinStr}
            onMaxChange={setDraftMaxStr}
          />
        </section>

        <footer className="poca-tag-modal__footer">
          <button
            type="button"
            className="poca-tag-modal__btn poca-tag-modal__btn--ghost"
            onClick={handleClear}
            disabled={!hasDraft}
          >
            Clear
          </button>
          <button
            type="button"
            className="poca-tag-modal__btn"
            onClick={handleApply}
          >
            Apply
          </button>
        </footer>
      </div>
    </div>
  );
}
